import {
  IntegrationEnum,
  IntegrationStatus,
} from 'models/InternalIntegrationSetting';
import useFetchIntegrationPropertySettings, {
  getIntegrationPropertySettingsQueryKey,
} from 'hooks/useFetchIntegrationPropertySettings';
import useEnableIntegrationMutation from 'pages/integrations/hooks/useEnableIntegrationMutation';
import useDisableIntegrationMutation, {
  IntegrationMutationLevel,
} from 'pages/integrations/hooks/useDisableIntegrationMutation';

const useToggleChannel = ({
  enumId,
  propertyUid,
}: {
  enumId: IntegrationEnum;
  propertyUid: string;
}) => {
  const { data, isLoading: isFetchingPropertySettings } =
    useFetchIntegrationPropertySettings({
      enumId,
      propertyUids: [propertyUid],
      enabled: !!propertyUid && !!enumId,
    });

  const { disableIntegration, isLoading: isDisablingIntegration } =
    useDisableIntegrationMutation();
  const { enableIntegration, isLoading: isEnablingIntegration } =
    useEnableIntegrationMutation();

  const propertySetting =
    data?.propertySettings.find(
      (setting) => setting.propertyUid === propertyUid,
    ) ?? null;

  const isActive = propertySetting?.status === IntegrationStatus.ACTIVE;
  const isPending = propertySetting?.status === IntegrationStatus.PENDING;
  // Pending is treated as active for the toggle
  const isEnabled = isActive || isPending;

  const queryKey = getIntegrationPropertySettingsQueryKey({
    enumId,
    propertyUids: [propertyUid],
  });

  const handleToggle = () => {
    if (isEnabled) {
      disableIntegration({
        level: IntegrationMutationLevel.PROPERTY,
        enumId,
        propertyUids: [propertyUid],
        appliesToAllProperties: false,
        queryKey,
      });
    } else {
      enableIntegration({
        level: IntegrationMutationLevel.PROPERTY,
        enumId,
        propertyUids: [propertyUid],
        appliesToAllProperties: false,
        queryKey,
      });
    }
  };

  return {
    isEnabled,
    isPending,
    handleToggle,
    isLoading: isFetchingPropertySettings,
    isUpdating: isDisablingIntegration || isEnablingIntegration,
    propertySetting,
  };
};

export default useToggleChannel;
